import React, { Fragment } from 'react';
import { GoogleMap } from '@react-google-maps/api';
import { useGoogleMaps } from '../../../../../common/useGoogleMaps';
import { getClosedGeocode } from '../../../../../../services/google/api';
import Place from '../../../../../../services/google/Place';
import SearchBox from './SearchBox';
import { makeStyles, createStyles } from '@material-ui/core';
import { convertLetter } from '../../common/Address';
import { changeLang, Lang } from '../../../../../../states/query';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      borderLeft: 'solid 1px #000000',
      borderRight: 'solid 1px #000000',
    },
  }),
);

interface Props {
  center: {
    lat: number;
    lng: number;
  },
  markerPoint: {
    lat: number;
    lng: number;
  },
  pointName: string,
  setPointName: (v: string) => void,
  setPlaceJP: (v: Place|null) => void,
  setCenter: (v: {
    lat: number;
    lng: number;
  }) => void,
  setMarkerPoint: (v: {
    lat: number;
    lng: number;
  }) => void,
  setMunicipality: (v: string) => void,
  switchEdited: (v: boolean) => void,
  setPlace: (v: Place) => void
}

/** タクシー・ハイヤー予約時の地点選択用の地図のコンポーネント。 */
export const Map: React.FC<Props> = (props: Props) => {
  const classes = useStyles();
  const containerStyle = {
    height: "300px",
    width: "100%",
  };
  const { i18n } = useTranslation();

  /** Google Mapsのスクリプトの読み込みが完了したかを表すフラグ。 */
  const { isLoaded } = useGoogleMaps();

  /** マーカーとして使用するピンのアイコンのパス。 */
  const pinIcon = "/icons/mapPin.png";

  /** Google MapsのDOMノードを格納する変数。 */
  let mapRef: GoogleMap | null = null;
  /** Google Mapsのマーカーを格納する変数。 */
  const markerRef = React.useRef<google.maps.Marker | null>(null);
  const [isMarkerReady, setIsMarkerReady] = React.useState(false);

  /** 中心座標が変更された場合の処理。 */
  // （マーカーを表示する座標のみ更新する。）
  // const onCenterChanged = () => {
  //   console.log("Drug!")
  //   // Google MapsのDOMノードが存在しない場合、以降の処理は実行しない。
  //   if (!mapRef) { return; }
  //   /** 地図上の中心地点の情報。 */
  //   const centerOnMap = mapRef.state.map?.getCenter();
  //   // 地図上の中心地点の情報が存在しない場合、以降の処理は実行しない。
  //   if (!centerOnMap) { return; }
  //   /** 地図上で表示されている中心座標。 */
  //   const coordinate = {
  //     lat: centerOnMap.lat(),
  //     lng: centerOnMap.lng(),
  //   };
  //   props.setMarkerPoint(coordinate);
  // }

  /** 地図上をクリックしたときの処理。 */
  const onClicked = () => {
    // 地図がクリックされた場合、編集済みフラグをオンにする。
    // （このフラグがオンの場合、利用空港が変更された際、初期位置が変更されない。）
    props.switchEdited(true);
  }

  /** ドラッグ操作が終わったタイミングの処理。 */
  const onDragEnd = async () => {
    // Google MapsのDOMノードが存在しない場合、以降の処理は実行しない。
    if (!mapRef) { return; }
    /** 地図上の中心地点の情報。 */
    const centerOnMap = mapRef.state.map?.getCenter();
    // 地図上の中心地点の情報が存在しない場合、以降の処理は実行しない。
    if (!centerOnMap) { return; }
    /** 地図上で表示されている中心座標。 */
    const coordinate = {
      lat: centerOnMap.lat(),
      lng: centerOnMap.lng(),
    }
    // ドラッグが終わったタイミングでマーカーをセットする
    props.setMarkerPoint(coordinate);

    const latLng = `${centerOnMap.lat()},${centerOnMap.lng()}`;
    /** 選択された地点の住所などを含んだ詳細情報。 */
    const selectedPlace = await getGeoDetail(latLng, changeLang(i18n.language));
    // 地点の詳細情報が取得できない場合（太平洋上などの場合）、以降の処理は実行しない。
    if (!selectedPlace) { return; }
    /** 選択された地点情報を更新する */
    props.setPlace(selectedPlace);
    /** 言語情報を取得する */
    const lang = changeLang(i18n.language);
    /** 選択された地点の住所。 */
    const address = convertLetter(selectedPlace.getFullAddress(lang).replace(/-/g, '_'));
    /** 「都道府県」・「市区町村」までの住所。 */
    const municipality = convertLetter(selectedPlace.getAddressToMunicipality());
    // 日本語以外の場合、日本語の住所を取得する
    // 日本語名称取得が完了してから中心地点を更新する
    if(i18n.language !== 'ja') {
      const selectedJPPlace = await getGeoDetail(latLng, Lang.ja);
      props.setPlaceJP(selectedJPPlace);
    }
    else {
      props.setPlaceJP(selectedPlace);
    }
    // 住所を選択された地点名として更新する。
    props.setPointName(address);
    // ドラッグ操作が終了したタイミングの中心座標を選択地点として更新する。
    props.setCenter(coordinate);
    // 市区町村までの住所を更新する。
    props.setMunicipality(municipality);
  }

  /**
   * 緯度経度から地点情報を取得する。
   * @param latLng 緯度経度の文字列。（例: 35.68153260987459,139.76771635598573）
   */
  async function getGeoDetail(latLng: string, lang: Lang) {
    const places: Place[] = await getClosedGeocode(latLng, lang);
    /** 取得できた地点情報の配列のうち1件目。 */
    const place = places[0];
    return place;
  }

  // クエリによって中心座標を変更する
  React.useEffect(() => {
    if (markerRef.current) {
      markerRef.current.setPosition(props.markerPoint);
      mapRef?.state.map?.panTo(props.markerPoint);
    }
  }, [props.markerPoint, isMarkerReady]);

  return (
    isLoaded ?
      <div className={classes.root} data-cy="google_map_area">
        <SearchBox
          pointName={props.pointName}
          setCenter={props.setCenter}
          setMarkerPoint={props.setMarkerPoint}
          setPointName={props.setPointName}
          setMunicipality={props.setMunicipality}
          switchEdited={props.switchEdited}
          setPlace={props.setPlace}
          setPlaceJP={props.setPlaceJP}
        />
        <GoogleMap
          center={props.markerPoint}
          ref={(ref) => {mapRef = ref}}
          // onCenterChanged={onCenterChanged}
          onDragEnd={onDragEnd}
          onClick={onClicked}
          mapContainerStyle={containerStyle}
          zoom={15}
          options={{
            fullscreenControl: false,
            mapTypeControl: false,
            streetViewControl: false,
            zoomControlOptions: {
              position: 8
            }
          }}
          onLoad={(map: google.maps.Map<Element>) => {
            // Marker を手動で追加
            markerRef.current = new google.maps.Marker({
              position: props.markerPoint,
              map: map,
              icon: pinIcon,
            });
            setIsMarkerReady(true);
          }}
        />
      </div> : <Fragment/>
  )
}

export default Map;
